import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Download, Code, Link2, Key, Copy } from "lucide-react";
import { decrypt, encrypt } from "../utils/cryptoUtils";
import { makeAuthenticatedRequest } from "../utils/api";
import { useDispatch } from "react-redux";
import { AxiosError } from "../components/sessionexpiry";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import "./Dashboard.css";

const DeveloperTools = ({ isSidebarCollapsed, isMobile }) => {
  const [payoutcallback, setpayoutcallback] = useState("");
  const [errors, setErrors] = useState({ payoutcallback: "" });
  const [showClientId, setShowClientId] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);

  const users = JSON.parse(sessionStorage.getItem("users"));
  const user_id = users?.userdatils?.kycs_step ? users.userdatils.user_id : "0";

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const payouthandlesubmit = (e) => {
    e.preventDefault();
    const newErrors = { payoutcallback: "" };
    let isValid = true;

    if (!payoutcallback.trim()) {
      newErrors.payoutcallback = "Webhook URL is required";
      isValid = false;
    } else {
      const urlPattern = /^(https?:\/\/)?([\w\-]+\.)+[\w\-]{2,}(\/\S*)?$/;
      if (!urlPattern.test(payoutcallback.trim())) {
        newErrors.payoutcallback = "Enter a valid URL";
        isValid = false;
      }
    }
    setErrors(newErrors);
    if (isValid) handleUpdate();
  };

  const handleUpdate = async () => {
    try {
      const updateJson = JSON.stringify({
        Dname: "payout",
        Tname: "merchants",
        Set: { webhook_url: payoutcallback, webhook_status: "1" },
        Where: { mid: user_id },
        Limit: "1",
      });

      const encrypted = encrypt(updateJson);
      const response = await makeAuthenticatedRequest(
        "POST",
        "/update",
        { encrypted },
        navigate
      );

      const decryptdata = decrypt(response);
      const jsonArray = JSON.parse(decryptdata.replace(/\\"/g, '"'));

      if (jsonArray["Status count"] === "200") {
        toast.success("Webhook updated successfully");
        setTimeout(() => window.location.reload(), 1000);
      } else {
        toast.error("Update failed");
      }
    } catch (err) {
      setError(err.message);
      AxiosError(navigate, err);
    }
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState({
    mid: "",
    webhook_url: "",
    client_id: "",
    client_secret: "",
  });

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const requestData = JSON.stringify({
          Dname: "payout",
          Tname: "merchants m",
          Cname: ["m.mid", "webhook_url", "client_id", "client_secret"],
          Where: [{ column: "m.mid", operator: "=", value: user_id }],
          Limit: "10",
          Offset: "0",
        });

        const encrypted = encrypt(requestData);
        const data = await makeAuthenticatedRequest(
          "POST",
          "/fetch",
          { encrypted },
          navigate
        );
        const decryptdata = decrypt(data);
        const jsonArray = JSON.parse(decryptdata.replace(/\\"/g, '"'));

        if (jsonArray.length > 0) {
          setProfileData({
            mid: jsonArray[1]["m.mid"] || "",
            webhook_url: jsonArray[1].webhook_url || "",
            client_id: jsonArray[1]["client_id"] || "",
            client_secret: jsonArray[1]["client_secret"] || "",
          });
        }
      } catch (err) {
        setError(err.message);
        AxiosError(navigate, err);
      }
    };
    fetchProfileData();
  }, [dispatch, user_id, navigate]);

  
  return (
    <div className="main-content animate-fade-in-up mesh-bg min-h-screen relative overflow-hidden pb-10">
      {/* Decorative Floating Color Orbs */}
      <div className="color-orb-1 top-10 left-10 pointer-events-none" />
      <div className="color-orb-2 bottom-20 right-10 pointer-events-none" />

      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-indigo-900 via-indigo-950 to-slate-950 rounded-3xl px-8 py-6 shadow-xl relative overflow-hidden border border-slate-900 animate-fade-in-up">
        {/* Ambient light leaks inside header */}
        <div className="absolute -top-24 -left-24 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10">
          <h2 className="text-3xl font-extrabold text-white tracking-tight flex items-center gap-2">
            <span>Developer Console</span>
            <span className="text-xs px-2.5 py-0.5 bg-indigo-500/25 border border-indigo-500/35 text-indigo-300 rounded-full font-bold uppercase tracking-wider animate-pulse">API V1</span>
          </h2>
          <p className="text-indigo-200/80 text-sm mt-1.5 font-medium">
            Retrieve integration credentials, access API documentations, and configure transaction webhooks.
          </p>
        </div>
        <nav className="mt-4 md:mt-0 relative z-10">
          <ol className="flex items-center space-x-2 text-xs font-semibold">
            <li>
              <Link to="/dashboard" className="flex items-center gap-1.5 py-2 px-3 bg-white/5 hover:bg-white/10 rounded-xl text-indigo-200 hover:text-white transition duration-200 border border-white/5">
                <span className="material-icons text-sm">dashboard</span>
                <span>Console</span>
              </Link>
            </li>
            <li className="text-indigo-400">/</li>
            <li className="text-indigo-300/60 font-normal">Developer Portal</li>
          </ol>
        </nav>
      </div>

      {/* Main content grid */}
      <div className="container mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column (2 Columns) */}
          <div className="lg:col-span-2 space-y-6">
            {/* API Access Panel */}
            <div className="glass-panel rounded-3xl border border-slate-100 p-6 sm:p-8 relative overflow-hidden animate-fade-in-up">
              {/* Accent top gradient line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-600 to-violet-600"></div>
              
              <div className="mb-8">
                <h3 className="text-xl font-extrabold text-slate-800">API Access</h3>
                <p className="text-xs text-slate-400 mt-1">Use these credentials to authenticate secure API requests</p>
              </div>
              
              <div className="space-y-5">
                {/* Client ID */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">X-CLIENT-ID</label>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-100/50">Private</span>
                  </div>
                  <div className="relative group">
                    <input
                      type={showClientId ? "text" : "password"}
                      value={profileData.client_id}
                      readOnly
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-3.5 pr-20 font-mono text-sm font-bold text-slate-800 premium-input transition-all"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                      <button
                        onClick={() => handleCopy(profileData.client_id)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
                        title="Copy Client ID"
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        onClick={() => setShowClientId(!showClientId)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
                        title="Show/Hide Client ID"
                      >
                        {showClientId ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Client Secret */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">X-CLIENT-SECRET</label>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-100/50">Private</span>
                  </div>
                  <div className="relative group">
                    <input
                      type={showClientSecret ? "text" : "password"}
                      value={profileData.client_secret}
                      readOnly
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-3.5 pr-20 font-mono text-sm font-bold text-slate-800 premium-input transition-all"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                      <button
                        onClick={() => handleCopy(profileData.client_secret)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
                        title="Copy Client Secret"
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        onClick={() => setShowClientSecret(!showClientSecret)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
                        title="Show/Hide Client Secret"
                      >
                        {showClientSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* WARNING BOX */}
              <div className="mt-6 rounded-2xl bg-amber-50/40 border border-amber-100/60 p-4 text-xs font-semibold text-amber-700 leading-relaxed flex items-start gap-2.5">
                <span className="material-icons text-amber-500 text-sm mt-0.5">warning</span>
                <span>Never expose your Client Secret publicly or commit it to shared code repositories.</span>
              </div>
            </div>

            {/* Webhook Configuration Panel */}
            <div className="glass-panel rounded-3xl border border-slate-100 p-6 sm:p-8 relative overflow-hidden animate-fade-in-up">
              {/* Accent top gradient line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 to-indigo-600"></div>

              <div className="mb-8">
                <h3 className="text-xl font-extrabold text-slate-800">Webhook Configuration (Payout)</h3>
                <p className="text-xs text-slate-400 mt-1">Configure endpoints to receive instant event payouts notifications</p>
              </div>

              <form onSubmit={payouthandlesubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Webhook URL <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                      <Link2 size={18} />
                    </div>
                    <input
                      type="text"
                      placeholder={profileData.webhook_url || "https://yourdomain.com/webhook"}
                      value={payoutcallback}
                      onChange={(e) => setpayoutcallback(e.target.value)}
                      className={`w-full rounded-2xl border pl-11 pr-4 py-3.5 text-sm premium-input transition-all ${
                        errors.payoutcallback
                          ? "border-rose-500 focus:ring-rose-500/20 focus:border-rose-500 bg-rose-50/10"
                          : "border-slate-200 bg-slate-50/50"
                      }`}
                    />
                  </div>
                  {errors.payoutcallback && (
                    <p className="text-rose-600 text-xs font-bold mt-2 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />{errors.payoutcallback}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-bold text-sm px-6 py-3 rounded-2xl shadow-md shadow-indigo-100 hover:shadow-lg hover:shadow-indigo-200 transform hover:-translate-y-0.5 active:scale-95 transition-all duration-200"
                >
                  Save Webhook Settings
                </button>
              </form>
            </div>
          </div>

          {/* Right Column (1 Column) */}
          <div className="lg:col-span-1 space-y-6">
            {/* Documentation Panel - Payout */}
            <a
              // href="https://caroset-files.s3.us-east-1.amazonaws.com/Payout_API_Documentation_v1.0.pdf"
              href="https://gaadiaa.s3.us-east-1.amazonaws.com/API_Documentation_V7.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="neon-glow-card glass-panel rounded-3xl border border-slate-100 p-6 sm:p-8 flex flex-col justify-between hover:border-indigo-200 transition duration-300 relative overflow-hidden group animate-fade-in-up"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-600 to-violet-600"></div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 group-hover:scale-110 transition duration-300 flex-shrink-0">
                  <Code size={24} />
                </div>
                <div>
                  <h4 className="font-extrabold text-slate-800 text-base">Payout API Documentation</h4>
                  <p className="text-xs text-slate-400 mt-1 leading-relaxed font-medium">
                    Download our complete developer integration guide covering Payout APIs, payloads, and request signing.
                  </p>
                </div>
              </div>
              <div className="mt-8 flex items-center justify-between pt-4 border-t border-slate-50">
                <span className="text-xs font-bold text-slate-400">PDF Document (v1.0)</span>
                <span className="text-xs font-extrabold text-indigo-600 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                  Download <Download size={14} />
                </span>
              </div>
            </a>

            {/* Documentation Panel - Payin */}
            {/* <a
              href="https://caroset-files.s3.us-east-1.amazonaws.com/Payin_API_Documentation_v1.0.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="neon-glow-card glass-panel rounded-3xl border border-slate-100 p-6 sm:p-8 flex flex-col justify-between hover:border-violet-200 transition duration-300 relative overflow-hidden group animate-fade-in-up"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 to-fuchsia-600"></div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center text-violet-600 group-hover:scale-110 transition duration-300 flex-shrink-0">
                  <Code size={24} />
                </div>
                <div>
                  <h4 className="font-extrabold text-slate-800 text-base">Payin API Documentation</h4>
                  <p className="text-xs text-slate-400 mt-1 leading-relaxed font-medium">
                    Download our complete developer integration guide covering Payin (Collection) APIs, payment gateways, and responses.
                  </p>
                </div>
              </div>
              <div className="mt-8 flex items-center justify-between pt-4 border-t border-slate-50">
                <span className="text-xs font-bold text-slate-400">PDF Document (v1.0)</span>
                <span className="text-xs font-extrabold text-violet-600 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                  Download <Download size={14} />
                </span>
              </div>
            </a> */}

            {/* Quick Start Panel */}
            <div className="glass-panel rounded-3xl border border-slate-100 p-6 sm:p-8 relative overflow-hidden flex flex-col justify-between animate-fade-in-up">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 to-indigo-600"></div>
              
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <span className="material-icons text-indigo-600 text-xl font-semibold">terminal</span>
                  Quick Start
                </h3>
                <p className="text-xs text-slate-400 mb-4 leading-relaxed font-medium">
                  Pass these header keys with every authorized payout request:
                </p>
                <div className="space-y-3 font-mono text-xs">
                  <div className="bg-slate-900 text-indigo-300 p-3.5 rounded-2xl border border-slate-950 shadow-inner relative group">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-sans font-bold mb-1">Authorization Headers</p>
                    <p className="text-indigo-400 font-semibold">X-CLIENT-ID: <span className="text-slate-200">your_id</span></p>
                    <p className="text-indigo-400 font-semibold mt-1">X-CLIENT-SECRET: <span className="text-slate-200">your_secret</span></p>
                  </div>
                  <div className="bg-slate-900 text-indigo-300 p-3.5 rounded-2xl border border-slate-950 shadow-inner">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-sans font-bold mb-1">Content Type</p>
                    <p className="text-slate-200 font-semibold">Content-Type: application/json</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 pt-5 border-t border-slate-100 text-xs font-bold text-slate-400 flex items-center gap-2">
                <span className="material-icons text-indigo-500 text-sm">help_outline</span>
                <span>Need help? Contact support</span>
              </div>
            </div>
          </div>

          
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default DeveloperTools;
